import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { usePinnedMessages } from '../../../../hooks/usePinnedMessages';
import { MESSAGE_TYPES } from '../../../../constants/messages';
import { stripReplyFallback } from '../../../../utils/replyUtils';
import './PinnedEventsBanner.css';

/**
 * Banner component that displays pinned messages at the top of chat view.
 * Shows one pinned message at a time with navigation controls when multiple exist.
 * Clicking the banner navigates to the pinned message in the timeline.
 *
 * @param {Object} props
 * @param {string} props.roomId - The room ID to display pinned messages for
 * @param {Array} props.members - Array of room members for sender lookup
 * @param {Function} props.onNavigateToMessage - Callback to scroll to pinned message in timeline
 */
const PinnedEventsBanner = ({
  roomId,
  members = [],
  onNavigateToMessage,
}) => {
  const { t } = useI18n();
  const { displayedEvents, isLoading } = usePinnedMessages(roomId);

  // Filter out redacted and missing events - they shouldn't appear in the banner
  const availableEvents = displayedEvents.filter(event => {
    if (event?.missing) return false;
    if (event?._sharedRedacted) return false;
    if (typeof event?.isRedacted === 'function' && event.isRedacted()) return false;
    return true;
  });

  // Track which pinned message is currently displayed
  const [currentIndex, setCurrentIndex] = useState(0);
  const bannerRef = useRef(null);

  // Reset currentIndex when availableEvents changes to prevent index out of bounds
  useEffect(() => {
    if (currentIndex >= availableEvents.length && availableEvents.length > 0) {
      setCurrentIndex(availableEvents.length - 1);
    } else if (availableEvents.length === 0) {
      setCurrentIndex(0);
    }
  }, [availableEvents.length, currentIndex]);

  // Shared index-wrapping helpers (used by button clicks and keyboard nav)
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev =>
      prev === 0 ? availableEvents.length - 1 : prev - 1,
    );
  }, [availableEvents.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev =>
      prev === availableEvents.length - 1 ? 0 : prev + 1,
    );
  }, [availableEvents.length]);

  const handlePrevious = useCallback((e) => {
    e.stopPropagation();
    goToPrevious();
  }, [goToPrevious]);

  const handleNext = useCallback((e) => {
    e.stopPropagation();
    goToNext();
  }, [goToNext]);

  const handleBannerClick = useCallback(() => {
    const currentPin = availableEvents[currentIndex];
    if (onNavigateToMessage && currentPin) {
      onNavigateToMessage(currentPin);
    }
  }, [onNavigateToMessage, availableEvents, currentIndex]);

  const handleKeyDown = useCallback((e) => {
    if (availableEvents.length <= 1) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goToNext();
    }
  }, [availableEvents.length, goToPrevious, goToNext]);

  /**
   * Get display text for message content based on message type
   * @param {Object} msgContent - The message content object
   * @returns {string} Display text for the message
   */
  const getMessagePreview = useCallback((msgContent) => {
    if (!msgContent) return t('PinnedMessages.contentFallback');

    // Strip reply fallback text from third-party clients
    const cleanBody = msgContent.body ? stripReplyFallback(msgContent.body) : '';

    switch (msgContent.msgtype) {
      case 'm.image':
        return cleanBody || t('PinnedMessages.imageFallback');
      case 'm.file':
        return cleanBody || t('PinnedMessages.fileFallback');
      case 'm.audio':
        return cleanBody || t('PinnedMessages.audioFallback');
      case 'm.video':
        return cleanBody || t('PinnedMessages.videoFallback');
      case 'm.location':
        return t('PinnedMessages.locationFallback');
      default:
        if (cleanBody) {
          return cleanBody.length > 50
            ? cleanBody.substring(0, 50) + '...'
            : cleanBody;
        }
        return t('PinnedMessages.contentFallback');
    }
  }, [t]);

  // Don't render if loading or no available pinned messages
  if (isLoading || availableEvents.length === 0) {
    return null;
  }

  // Compute derived values for rendering
  const currentPin = availableEvents[currentIndex];
  // Use availableEvents.length for consistency - counter should match what's navigable
  const hasMultiple = availableEvents.length > 1;
  const senderId = currentPin?.getSender?.();
  const sender = members.find(m => m.userId === senderId || m.id === senderId);
  const content = currentPin?.getClearContent?.() || currentPin?.getContent?.() || {};
  const isEncryptedNoKeys = currentPin?.getType?.() === 'm.room.encrypted' && !currentPin?.getClearContent?.();
  const isEmote = !isEncryptedNoKeys && content.msgtype === MESSAGE_TYPES.EMOTE;

  return (
    <div
      className="pinned-events-banner"
      onClick={handleBannerClick}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label={t('PinnedMessages.pinnedMessages')}
      ref={bannerRef}
      tabIndex={0}
    >
      {/* Live region for screen reader announcements */}
      <span
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {hasMultiple ? `${currentIndex + 1} of ${availableEvents.length}` : ''}
      </span>

      {/* Previous navigation button - only show for multiple pins */}
      {hasMultiple && (
        <button
          className="pinned-banner-nav prev"
          onClick={handlePrevious}
          type="button"
          aria-label={t('PinnedMessages.previousPin')}
        >
          <ion-icon name="chevron-back" />
        </button>
      )}

      {/* Pin icon */}
      <img src='/images/svg/pin-bar.svg' alt='' aria-hidden='true' className='pinned-banner-icon' />

      {/* Message content preview */}
      <div className="pinned-banner-content">
        <span className="pinned-banner-sender">
          {isEmote ? '* ' : ''}
          {sender?.displayName || sender?.name || currentPin?.sender?.rawDisplayName || currentPin?.sender?.name || senderId?.split(':')[0]?.substring(1) || t('PinnedMessages.unknownSender')}
          {isEmote ? '' : ':'}
        </span>
        <span className={`pinned-banner-text${isEmote ? ' pinned-banner-emote' : ''}`}>
          {isEncryptedNoKeys ? t('PinnedMessages.encryptedMessage') || 'Encrypted message' : getMessagePreview(content)}
        </span>
      </div>

      {/* Counter and next navigation - only show for multiple pins */}
      {hasMultiple && (
        <>
          <span className="pinned-banner-counter">
            ({currentIndex + 1}/{availableEvents.length})
          </span>
          <button
            className="pinned-banner-nav next"
            onClick={handleNext}
            type="button"
            aria-label={t('PinnedMessages.nextPin')}
          >
            <ion-icon name="chevron-forward" />
          </button>
        </>
      )}
    </div>
  );
};

PinnedEventsBanner.propTypes = {
  roomId: PropTypes.string.isRequired,
  members: PropTypes.array.isRequired,
  onNavigateToMessage: PropTypes.func.isRequired,
};

export default PinnedEventsBanner;
